'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import {observer} from "mobx-react";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button  from 'react-bootstrap/Button';

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {Campuses} from '@uw-it-sis/lib-react/lib/AppConstants';
import {capitalize} from '@uw-it-sis/lib-react/lib/Utils';

import preferredCampusStore from './PreferredCampusStore';

const analyticsStore = appStore.getAnalyticsStore();

/**
 * Campus selection widget.
 *
 * The selected campus can be passed in via the 'campus' property. If the prop is empty the value from AppStore is used.
 * If that is empty to then the panel will be open with no selection.
 */
class CampusSelector extends React.Component {

    constructor(props) {
        super(props);
        this.props = props;

        /*
         * displayCampusPanel is used to toggle the display of the campus selections.
         * If a 'campus' prop exists displayCampusPanel is false else true.
         */
        this.state = {
            displayCampusPanel: ! this.props.campus
        };

        this.store = preferredCampusStore;

        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        this.store.mount(this.props.campus);
    }

    componentDidUpdate(prevProps) {
        // If the campus changes the re-init the store.
        if (prevProps.campus !== this.props.campus) {
            this.store.mount(this.props.campus);
        }
    }

    /**
     * Handler for campus changes.
     */
    onChange = (changeEvent) => {
        if (this.store.campus !== changeEvent.target.value) {
            let oldCampus = this.store.campus;
            let newCampus = changeEvent.target.value;
            this.store.updatePreferredCampus(newCampus);
            // Analytics for change of campus. An application label will be added during processing so no need put it here.
            analyticsStore.addEvent("campus-selection", {}, {from: oldCampus, to: newCampus});
        }
    };

    /**
     * Toggles the campus panel display. User must manually close.
     */
    toggleCampusPanel = () => {
        let nextVal = ! this.state.displayCampusPanel;
        this.setState({
            displayCampusPanel: nextVal
        });
    };

    render() {
        let campusHeading;
        let selectCampus;
        let changeCampusLink;

        let campus = this.store.campus;

        const campuses = Object.keys(Campuses).sort((a, b) => {
            return a === "seattle" ? -1 : (b === "seattle" ? 1 : a.localeCompare(b))
        });

        let checkOptions = campuses.map((site, index) => {
            return (
                <Form.Check inline
                            key={index}
                            id={`${site}-campus-selection`}>
                    <Form.Check.Input
                        type="radio"
                        name="campus"
                        checked={campus === site}
                        aria-checked={campus === site}
                        onChange={this.onChange}
                        autoFocus={campus === site}
                        value={site}
                    />
                    <Form.Check.Label>
                        {capitalize(site)} Campus
                    </Form.Check.Label>
                </Form.Check>
            )
        })

        // By default a form with the radio selection to campus is displayed.
        let form =
            <fieldset role="group" aria-activedescendant={campus ? campus + '-campus-selection':''}>
                <legend className="sr-only">Select your preferred campus (Required)</legend>
                <Form.Group controlId="campus-selector" className="mt-2">
                    {checkOptions}
                </Form.Group>
            </fieldset>;

        if (campus) {
            // If a selection for campus already exists then we hide the form and display a change campus link.
            if (! this.state.displayCampusPanel) {
                form = "";
                changeCampusLink =
                    <Button id="campus-change-selection-open"
                            variant="inline-link"
                            onClick={this.toggleCampusPanel}
                            aria-controls="campus-selection"
                            aria-expanded="false">
                        Change Campus
                    </Button>
            } else {
                changeCampusLink =
                    <Button id="campus-change-selection-close"
                            variant="inline-link"
                            onClick={this.toggleCampusPanel}
                            aria-controls="campus-selection"
                            aria-expanded="true">
                        Close
                    </Button>;
            }

            campusHeading =
                <Col xs={12}>
                    <span className="page-subheading">{capitalize(campus)} Campus</span>
                    <span className="ms-4">{changeCampusLink}</span>
                </Col>;

        } else {
            // In the initial scenario where a campus does not exist for a user we display this instructional message.
            selectCampus =
                <Col xs={12}>
                    <label className="text-success fw-bold">Select your preferred campus</label>
                </Col>
        }

        return (
            <div>
                <Row>
                    {campusHeading}
                    {selectCampus}
                    <Col xs={12}>
                        <form id="campus-selection" aria-live="polite">
                            {form}
                        </form>
                    </Col>
                </Row>
            </div>
        );
    }
}

CampusSelector.propTypes = {
    campus: PropTypes.string
};

export default observer(CampusSelector);